<script lang="ts">
  import Attribution from "../Attribution.svelte";
  import { fetchAttribution, showToastWrapper } from "../global";
  import playIcon from "../assets/play.svg?raw";
  import tagIcon from "../assets/tag.svg?raw";
  import stopIcon from "../assets/stop.svg?raw";
  import editIcon from "../assets/edit.svg?raw";
  import { stopPropagation } from "../genericHandlers";
  import { onMount } from "svelte";
  import LibrarySoundFavourite from "./LibrarySoundFavourite.svelte";
  import { libraryLayout, LibraryLayouts, isNewSound } from "./Library";
  import { captureDoubleClickAndCall, outclick } from "../lib/interactions";
  import { userInfo } from "../store";
  import { notifyWhenMounted } from "../tutorial/Tutorial";

  export let publicSound;
  export let playFn;
  export let stopFn;
  export let soundUpdatedCallback: (publicSound) => void;

  let attribution = null;
  function isUserSound(publicSound) {
    return publicSound.CreatedByUsername === $userInfo.username;
  }

  onMount(() => {
    fetchAttribution(publicSound.Data.id, (att) => (attribution = att));
  });

  let playing = false;
  const handlePlay = () => {
    playFn(() => {
      stop();
    }, publicSound);
    playing = true;
  };

  function stop() {
    playing = false;
  }

  const handleStop = () => {
    stopFn();
    playing = false;
  };

  //TODO BL: Remove
  $: stringTags = publicSound.Tags.filter((tag) => {
    return tag !== "Music" && tag !== "Ambience" && tag != "Sound Effect";
  }).join(", ");

  let editing = false;
  const startEditing = () => {
    if (isUserSound(publicSound)) {
      editing = true;
    }
  };

  let nameInput = null;
  function handleNameChangeRequest(evt) {
    const newSound = structuredClone(publicSound);
    newSound.Name = nameInput.value;
    evt.preventDefault();
    fetch("/app/library/sound/update", {
      method: "POST",
      body: JSON.stringify(newSound),
      headers: {
        "Content-type": "application/json, charset=UTF-8",
      },
    })
      .then((response) => {
        if (!response.ok) {
          showToastWrapper(
            "Something went wrong while updating the sound name. Please make sure the name is unique, refresh, and try again.",
            true,
          );
          return;
        }
        publicSound = newSound;
        soundUpdatedCallback(newSound);
      })
      .catch((err) => {
        showToastWrapper(
          "Something went wrong while updating the sound name. Please make sure the name is unique, refresh, and try again.",
          true,
        );
      })
      .finally(() => {
        editing = false;
      });
  }

  function handleKeys(evt) {
    switch (evt.key) {
      case "Submit":
        handleNameChangeRequest(evt);
        break;
      case "Enter":
        handleNameChangeRequest(evt);
        break;
      case "Esc":
        editing = false;
        break;
    }
  }
</script>

<div
  class="sound border-solid-2px border-blue border-radius-pico-border-radius padding-top-0-5rem padding-bottom-0-5rem padding-left-1rem padding-right-1rem background-color-pico-background"
  use:notifyWhenMounted
>
  <div class="sound-title-container font-family-bardic-sans editable-icon">
    <div>
      {#if editing}
        <form on:submit={handleNameChangeRequest}>
          <input
            bind:this={nameInput}
            type="text"
            name="sound-name"
            value={publicSound.Name}
            class="soundboard-sound-title-input"
            on:keydown={handleKeys}
            on:pointerdown={stopPropagation}
            on:mousedown={stopPropagation}
            use:outclick={handleNameChangeRequest}
          />
        </form>
      {:else}
        <div
          class="sound-title editable-icon"
          tabindex="0"
          on:dblclick={startEditing}
          on:pointerdown={(evt) => {
            captureDoubleClickAndCall(evt, startEditing);
          }}
        >
          {publicSound.Name}
          {#if isUserSound(publicSound)}
            <div
              class="hover-target width-1rem"
              tabindex="0"
              on:click={startEditing}
              on:pointerdown={stopPropagation}
              on:mousedown={stopPropagation}
            >
              {@html editIcon}
            </div>
          {/if}
        </div>
      {/if}
    </div>
    <span>
      <Attribution {attribution} isUserSound={isUserSound(publicSound)} />
    </span>
  </div>
  <!-- TODO BL: Uncomment when implemented -->
  <!-- <div class="time font-family-bardic-sans blue"></div> -->
  <div class="favourite">
    <LibrarySoundFavourite />
  </div>
  <div class="controls">
    {#if playing}
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <!-- svelte-ignore a11y-no-static-element-interactions -->
      <div
        id="stop-{publicSound.Id}"
        class="soundcard-button pico-color"
        on:click={handleStop}
        on:pointerdown={stopPropagation}
        on:mousedown={stopPropagation}
      >
        {@html stopIcon}
      </div>
    {:else}
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <!-- svelte-ignore a11y-no-static-element-interactions -->
      <div
        id="play-{publicSound.Id}"
        class="soundcard-button pico-color"
        on:click={handlePlay}
        on:pointerdown={stopPropagation}
        on:mousedown={stopPropagation}
      >
        {@html playIcon}
      </div>
    {/if}
  </div>
  <!-- //TODO BL: Remove -->
  {#if $libraryLayout === LibraryLayouts.DockedRight || $libraryLayout === LibraryLayouts.Mobile}
    <div class="tags flex gap-5px font-family-bardic-sans">
      <span class="blue width-1rem text-align-bottom line-height-1rem"
        >{@html tagIcon}</span
      ><span class="text-align-bottom line-height-1rem font-size-small blue"
        >{stringTags}</span
      >
    </div>
  {/if}
  {#if isNewSound(publicSound)}
    <div
      class="new-dot font-size-small border-blue font-family-bardic-sans background-color-blue padding-right-5px padding-left-5px dark"
    >
      New!
    </div>
  {/if}
</div>
